import { useCallback, useState } from "react";
import { Pressable } from "react-native";
import { Card, Text, View } from "@src/components";
import type { ImageProvider } from "../imageProvider";
import { collectPriorityIncidents } from "../priority";
import { type PriorityIncident } from "../priority";
import { formatSiteHeading } from "../siteDisplay";
import type { WasteSenseEventRow, WasteSenseSite } from "../types";
import { EventSnapshotLightbox } from "./EventSnapshotLightbox";
import { LazySnapshot } from "./LazySnapshot";
import { formatEventTimestamp } from "./eventDisplay";

interface PriorityIncidentsHeroProps {
  /**
   * Already-ordered sites — the hero scans them for priority rows
   * via `isPriorityIncident` and renders a newest-first roll-up.
   */
  sites: readonly WasteSenseSite[];
  /** Resolves snapshot URLs for the row thumbnails. */
  imageProvider: ImageProvider;
  /**
   * Optional handler invoked when an operator clicks "Locate on map".
   * The detail screen wires this to the priority map's focus state so
   * the matching marker pulses and the corresponding list row scrolls
   * into view.
   */
  onLocateOnMap?: (assetId: string) => void;
}

/**
 * "Priority incidents for the week" hero block.
 *
 * Mirrors the legacy report's `<div class="urgent">` block: a
 * coral-bordered halo when there's anything to surface, a roll-up
 * heading with the total count, and a snapshot-led list of every
 * priority incident (newest-first) with site, time, camera and
 * description. Falls through to a single "none detected" row when
 * the predicate doesn't match anything.
 *
 * The list itself isn't virtualised — priority incidents are
 * expected to be a small fraction of the events in the window,
 * usually under a dozen even on the noisiest sites. If that ever
 * changes we can drop a `LegendList` in here without touching the
 * predicate or the row component.
 */
export function PriorityIncidentsHero({ sites, imageProvider, onLocateOnMap }: PriorityIncidentsHeroProps) {
  const incidents = collectPriorityIncidents(sites);
  const total = incidents.length;
  const hasIncidents = total > 0;

  const [active, setActive] = useState<{ event: WasteSenseEventRow; imageUrl: string; timezone: string } | null>(null);

  const handleOpenSnapshot = useCallback((incident: PriorityIncident, imageUrl: string) => {
    setActive({ event: incident.row, imageUrl, timezone: incident.site.timezone });
  }, []);
  const handleCloseSnapshot = useCallback(() => setActive(null), []);

  return (
    <View gap="md">
      <Card
        px="lg"
        py="lg"
        borderColor={hasIncidents ? "danger" : "borderCard"}
        /**
         * `borderWidth` is a deliberate visual override of the
         * default 1px card border so the "hot" state reads as
         * urgent at a glance — same idea as the legacy report's
         * `.urgent-hot` halo, just expressed via the design
         * system primitives.
         */
        style={hasIncidents ? { borderWidth: 2 } : undefined}
      >
        <View gap="md">
          <Text textStyle="xs/semibold" color={hasIncidents ? "danger" : "textMuted"} textTransform="uppercase">
            {hasIncidents ?
              `Priority incidents for the week: ${total.toLocaleString()} detected`
            : "Priority incidents for the week: none detected"}
          </Text>

          {hasIncidents ?
            <View gap="md">
              {incidents.map((incident) => (
                <PriorityIncidentRow
                  key={incident.row.event_id}
                  incident={incident}
                  imageProvider={imageProvider}
                  onLocateOnMap={onLocateOnMap}
                  onOpenSnapshot={handleOpenSnapshot}
                />
              ))}
            </View>
          : <Text textStyle="sm/regular" color="text">
              No potential dumping incidents were detected in this weekly reporting window.
            </Text>
          }
        </View>
      </Card>

      <EventSnapshotLightbox
        event={active?.event ?? null}
        imageUrl={active?.imageUrl ?? null}
        timezone={active?.timezone ?? "UTC"}
        onClose={handleCloseSnapshot}
      />
    </View>
  );
}

interface PriorityIncidentRowProps {
  incident: PriorityIncident;
  imageProvider: ImageProvider;
  onLocateOnMap?: (assetId: string) => void;
  onOpenSnapshot: (incident: PriorityIncident, imageUrl: string) => void;
}

const THUMB_W = 160;
const THUMB_H = 96;

/**
 * One row in the urgent list: snapshot thumbnail on the left, then
 * a stacked metadata column with time, site, camera, description
 * and a "Locate on map" affordance.
 *
 * Mirrors the legacy `.urgent-item` markup field-for-field so the
 * static and live reports describe the same incident the same way.
 */
function PriorityIncidentRow({ incident, imageProvider, onLocateOnMap, onOpenSnapshot }: PriorityIncidentRowProps) {
  const { row, site } = incident;
  const imageUrl = imageProvider.getEventImageUrl(row, { site });
  const timeLabel = formatEventTimestamp(row.timestamp, site.timezone);
  const siteLabel = formatSiteHeading(site.title);
  const cameraLabel = `${row.asset_id} · ${row.camera}`;
  const description = row.description.trim() || "No description";

  const assetId = row.asset_id || site.assets[0] || site.title;

  return (
    <View flexDirection="row" gap="md" alignItems="flex-start" style={{ flexWrap: "wrap" }}>
      {imageUrl ?
        <Pressable
          onPress={() => onOpenSnapshot(incident, imageUrl)}
          accessibilityRole="button"
          accessibilityLabel={`Open snapshot for ${siteLabel}`}
          style={
            {
              width: THUMB_W,
              height: THUMB_H,
              borderRadius: 6,
              overflow: "hidden",
              cursor: "zoom-in",
            } as object
          }
        >
          <LazySnapshot uri={imageUrl} width={THUMB_W} height={THUMB_H} alt={`Snapshot for ${siteLabel}`} />
        </Pressable>
      : <View
          style={{
            width: THUMB_W,
            height: THUMB_H,
            borderRadius: 6,
            alignItems: "center",
            justifyContent: "center",
          }}
          bg="surfaceCardInner"
        >
          <Text textStyle="xs/regular" color="textMuted">
            Snapshot unavailable
          </Text>
        </View>
      }

      <View flex={1} gap="xs" style={{ minWidth: 240 }}>
        <View flexDirection="row" gap="sm" style={{ flexWrap: "wrap" }} alignItems="baseline">
          <Text textStyle="xs/semibold" color="text">
            {timeLabel}
          </Text>
          <Text textStyle="xs/regular" color="textMuted">
            ·
          </Text>
          <Text textStyle="xs/semibold" color="text">
            {siteLabel}
          </Text>
          <Text textStyle="xs/regular" color="textMuted">
            ·
          </Text>
          <Text textStyle="xs/regular" color="textMuted">
            {cameraLabel}
          </Text>
        </View>

        <Text textStyle="sm/regular" color="text">
          {description}
        </Text>

        {onLocateOnMap ?
          <Pressable
            onPress={() => onLocateOnMap(assetId)}
            accessibilityRole="button"
            accessibilityLabel={`Locate ${siteLabel} on the priority map`}
          >
            <Text textStyle="xs/semibold" color="text">
              Locate on map →
            </Text>
          </Pressable>
        : null}
      </View>
    </View>
  );
}
